import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import {
  Database,
  ref,
  objectVal,
  listVal,
  update,
  set,
  get,
  push,
  query,
  orderByChild,
  orderByKey,
  startAt,
  startAfter,
  endAt,
  endBefore,
  limitToFirst,
  limitToLast,
} from "@angular/fire/database";
import { AuthService } from "./auth.service";
import { TranslateService } from "@ngx-translate/core";
import { UtilService } from "./util.service";
import { TrackingService } from "./tracking.service";
import { ApiService } from "./api.service";
import { StorageService } from "./storage.service";
import { Md5 } from "ts-md5/dist/md5";
import { defaults } from "../shared/defaults";
import { BvDialogService, BvToastService } from "../design-system";
import { ConfigService } from "./config.service";
import { AlertDialogComponent } from "../shared/alert-dialog/alert-dialog.component";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { Observable, of, switchMap, filter, map } from "rxjs";
import {
  ClubRole,
  Capability,
  ROLE_CAPS,
  ROLE_LABEL_KEY,
  roleHasCapability,
} from "./permissions";

// Long-edge caps for club brand images (format kept, see uploadBrandImage).
// Backgrounds fill 4K clock screens; logos never show larger than ~1000 px.
const CLUB_LOGO_MAX_EDGE = 1024;
const CLUB_BACKGROUND_MAX_EDGE = 3840;

@Injectable({ providedIn: "root" })
export class ClubService {
  private readonly mdb = inject(Database); // modular — signals + clubAF writes
  private readonly injector = inject(EnvironmentInjector);
  private readonly storageServ = inject(StorageService);

  /** Current club id. `init()` sets it; the streams below re-key off it. */
  readonly selectedClubId = signal<string | null>(null);

  // --- Live club data (modular AngularFire, keyed on selectedClubId) ---
  readonly club = toSignal(
    toObservable(this.selectedClubId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              objectVal<any>(ref(this.mdb, `clubs/${id}`))
            )
          : of(null)
      )
    ),
    { initialValue: null as any }
  );
  /** A child map of the club object as a list, in key order — what the
   *  former `listVal(ref(clubs/{id}/<child>))` returned. Those listeners
   *  started with rxfire's one-shot get(), which only hits the cache once the
   *  data has arrived, so at lobby load every child was fetched from the server
   *  again next to the full club listener (members alone is ~300 KB on the
   *  biggest clubs). Deriving from `club` reads each byte once. */
  private childList(child: string) {
    return computed(() => {
      const map = this.club()?.[child];
      return map ? (Object.values(map) as any[]) : ([] as any[]);
    });
  }
  // Derived from the club object, which already carries the whole
  // clubs/{id}/tournaments mirror. This used to be a second listener, an
  // orderByChild("tournTimestamp") query on the same path, and a query can't
  // be served from the parent listener's cache, so every lobby load downloaded
  // the mirror twice (221 KB + 217 KB for a mid-sized club in the profiler,
  // about 10% of all RTDB egress). Same order as that query: oldest first,
  // entries without a timestamp first.
  private readonly tournamentsRaw = computed(() => {
    const mirror = this.club()?.tournaments;
    if (!mirror) return [] as any[];
    return Object.values(mirror).sort(
      (a: any, b: any) => (a?.tournTimestamp ?? 0) - (b?.tournTimestamp ?? 0)
    );
  });
  /** Tournaments, dropping placeholder entries with no tournID. */
  readonly tournaments = computed(() =>
    (this.tournamentsRaw() ?? []).filter((t: any) => t && t.tournID)
  );
  private readonly leaguesRaw = this.childList("leagues");
  // Sorted client-side by `sort` — there is no `.indexOn: "sort"` rule on
  // clubs/$cid/leagues, and the modular SDK *throws* on an unindexed
  // orderByChild query (compat only warned + returned data). Few leagues, so
  // a client-side sort is cheap and removes the rules dependency.
  readonly leagues = computed(() =>
    [...(this.leaguesRaw() ?? [])].sort(
      (a: any, b: any) => (a?.sort ?? 0) - (b?.sort ?? 0)
    )
  );
  private readonly locationsRaw = this.childList("locations");
  /** Club venues (clubs/{id}/locations), newest first. Client-side sort — same
   *  reasoning as leagues (no .indexOn, modular SDK throws on unindexed query). */
  readonly locations = computed(() =>
    [...(this.locationsRaw() ?? [])]
      .filter((l: any) => l && l.locationID)
      .sort((a: any, b: any) => (a?.sort ?? 0) - (b?.sort ?? 0))
  );
  /** The club's home venue, or null when unset or since deleted. */
  readonly homeLocation = computed(() => {
    const id = this.club()?.homeLocationID;
    if (!id) return null;
    return this.locations().find((l: any) => l.locationID === id) ?? null;
  });
  readonly members = this.childList("members");
  readonly templates = this.childList("templates");
  readonly joinRequests = this.childList("joinRequests");

  // --- Legacy field getters (consumers unchanged) ---
  get latestClub() {
    return this.club();
  }
  get latestTournaments() {
    return this.tournaments();
  }
  get latestLeagues() {
    return this.leagues();
  }
  get latestLocations() {
    return this.locations();
  }
  get latestMembers() {
    return this.members();
  }
  get latestTemplates() {
    return this.templates();
  }
  get latestJoinRequests() {
    return this.joinRequests();
  }
  get clubID(): string {
    return this.selectedClubId()!;
  }
  get clubOwner(): boolean {
    return this.isClubOwner();
  }
  get clubStaff(): boolean {
    return this.isClubStaff();
  }

  // --- Capability layer (see providers/permissions.ts) ---
  /**
   * Current user's effective role in the active club. The `club.owner` field is
   * the source of truth for ownership (the member record mirrors it but the
   * field wins); any unrecognized stored role fails closed to Member.
   */
  readonly currentRole = computed<ClubRole | null>(() => {
    const club = this.club();
    const uid = this.auth.uid;
    if (!club || !uid) return null;
    if (club.owner && club.owner == uid) return ClubRole.Owner;
    const m = club.members && club.members[uid];
    const r = m?.role as ClubRole | undefined;
    return r && ROLE_CAPS[r] ? r : ClubRole.Member;
  });

  /** Capability check consulted by templates and component logic. */
  can(cap: Capability): boolean {
    return roleHasCapability(this.currentRole(), cap);
  }

  // Convenience getters so templates read a typo-proof property instead of a
  // capability string literal.
  get canClock(): boolean {
    return this.can(Capability.CLOCK_CONTROL);
  }
  get canSeat(): boolean {
    return this.can(Capability.SEATING_OPS);
  }
  get canRegister(): boolean {
    return this.can(Capability.REGISTER_PLAYERS);
  }
  get canEliminate(): boolean {
    return this.can(Capability.ELIMINATE_PLAYER);
  }
  get canEditStack(): boolean {
    return this.can(Capability.EDIT_STACK);
  }
  get canPrint(): boolean {
    return this.can(Capability.PRINT_TICKET);
  }
  get canUndo(): boolean {
    return this.can(Capability.UNDO);
  }
  get canConfig(): boolean {
    return this.can(Capability.CONFIG_TOURNAMENT);
  }
  get canManageMembers(): boolean {
    return this.can(Capability.MANAGE_MEMBERS);
  }
  get canConfigLocations(): boolean {
    return this.can(Capability.CONFIG_LOCATION);
  }

  /**
   * Roles the current user may assign to other members. Anyone who can manage
   * members (Director/Owner) may assign Director-and-below — i.e. anything
   * except Owner. (Owner is set only via the admin-only changeOwner transfer.)
   * Returns [] for everyone else.
   */
  assignableRoles(): ClubRole[] {
    if (!this.canManageMembers) return [];
    return [
      ClubRole.Staff,
      ClubRole.Floor,
      ClubRole.TableCaptain,
      ClubRole.Member,
    ];
  }

  /**
   * i18n key for a stored role's display label, for use with the translate pipe
   * (e.g. `clubServ.roleLabelKey(member.role) | translate`). Reactive to the
   * active language — unlike the old prebuilt label map. Unknown/missing roles
   * fall back to the Member label.
   */
  roleLabelKey(role: string | null | undefined): string {
    return ROLE_LABEL_KEY[role as ClubRole] || ROLE_LABEL_KEY[ClubRole.Member];
  }

  // --- Legacy write handles (clubAF.update is also called by League/Tournament) ---
  get clubAF() {
    const id = this.selectedClubId();
    return {
      update: (data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `clubs/${id}`), data)
        ),
    };
  }
  // --- Modular write/read helpers (replace the old rootRef/pubRef/clubRef
  // compat refs). Multi-path fan-out updates write at the root with slash-keyed
  // paths; single-node ops take an explicit path. All wrapped in the injection
  // context (AngularFire zone-stability requirement). ---
  private rootUpdate(updates: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb), updates)
    );
  }
  private nodeUpdate(path: string, data: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, path), data)
    );
  }
  private nodeSet(path: string, data: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, path), data)
    );
  }
  private nodeGet(path: string): Promise<any> {
    return runInInjectionContext(this.injector, () => get(ref(this.mdb, path)));
  }
  private nodePush(path: string, data: any): any {
    return runInInjectionContext(this.injector, () =>
      push(ref(this.mdb, path), data)
    );
  }
  /** Legacy AngularFireList-style key update for members (members.component). */
  get membersAF() {
    const id = this.selectedClubId();
    return {
      update: (key: string, data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `clubs/${id}/members/${key}`), data)
        ),
    };
  }

  // --- Per-club re-broadcast streams (consumers subscribe once) ---
  readonly club$ = toObservable(this.club);
  readonly leagues$ = toObservable(this.leagues);
  readonly locations$ = toObservable(this.locations);
  readonly tournaments$ = toObservable(this.tournaments);
  readonly members$ = toObservable(this.members);
  readonly clubInitObs = toObservable(this.club).pipe(
    filter((c) => !!c),
    map((c: any) => c.clubID)
  );

  // --- Plain mutable state (unchanged) ---
  lobbyIndex: number = 0;
  currentLeague: any;
  saveMemberProcessing: boolean = false;
  editMember: any;
  // Set in async (deleteTournament / denyRequest .then) and read in templates →
  // signal-backed so they reflect under zoneless (get/set = zero call-site churn).
  private _loadingID = signal<string | null>(null);
  get loadingID(): string | null { return this._loadingID(); }
  set loadingID(v: string | null) { this._loadingID.set(v); }
  deletedClub!: string;
  membersOrderPerPage!: any[];
  loadingItemsOrderPerPage: boolean = false;
  tournamentsOrderPerPage!: any[];
  private _loadingDenyRequest = signal(false);
  get loadingDenyRequest() { return this._loadingDenyRequest(); }
  set loadingDenyRequest(v: boolean) { this._loadingDenyRequest.set(v); }
  private paginationSub: any;

  constructor(
    private auth: AuthService,
    private translate: TranslateService,
    private util: UtilService,
    private tracking: TrackingService,
    private snackBar: BvToastService,
    private dialog: BvDialogService,
    private configServ: ConfigService,
    private api: ApiService
  ) {
    // On club load (owner only), reconcile the club's stored subscription
    // level/limit with the owner's current plan. (Was inline in the old club
    // subscribe.)
    effect(() => {
      const club = this.club();
      if (club && club.owner) this.updateClubLevel(club.owner);
    });
  }

  updateClubLevel(owner: string) {
    if (owner == this.auth.uid) {
      // Leave a club with an active temporary comp upgrade alone — the server
      // owns its subLevel/player_limit until the comp expires. Reconciling here
      // would revert the comp (the club's values intentionally differ from the
      // owner's plan). An expired comp is ignored, so reconciliation resumes.
      const comp = this.latestClub && this.latestClub.compUpgrade;
      if (comp && comp.expiresAt > Date.now()) return;
      this.nodeGet(
        `profiles/${this.auth.uid}/subscription/code`
      ).then((snapshot) => {
          let planID: string = snapshot.val();
          if (!planID) planID = "KITCHEN_TABLE";
          let plan: any = this.configServ.getPlan(planID);
          if (
            (plan && this.latestClub.subLevel != plan.level) ||
            this.latestClub.player_limit != plan.player_limit
          )
            this.clubAF.update({
              subLevel: plan.level,
              player_limit: plan.player_limit,
            });
        });
    }
  }
  /** Select the active club (legacy name `init`). Streams re-key automatically. */
  init(clubID: string) {
    this.selectedClubId.set(clubID);
    this.lobbyIndex = 0;
    this.getFirstItemsPage("tournaments", 8);
    return clubID;
  }
  private isClubOwner() {
    return this.currentRole() === ClubRole.Owner;
  }
  // "Staff" historically meant Owner|Staff (now Owner|Director). Floor and
  // TableCaptain are NOT staff — config/member/undo gates that still read
  // `clubStaff` therefore correctly exclude the new mid/low tiers.
  private isClubStaff() {
    const r = this.currentRole();
    return r === ClubRole.Owner || r === ClubRole.Staff;
  }
  noClub() {
    this.selectedClubId.set(null);
  }
  deleteTournament(tournID: string) {
    // The tournaments signal updates from the RTDB round-trip; no optimistic
    // local splice (the snapshot is now immutable).
    this.loadingID = tournID;
    setTimeout(() => {
      this.api
        .deleteTournament(tournID)
        .then((res) => {
          this.loadingID = "";
        })
        .catch((err: any) => {
          // Structured { code } errors translate to a readable toast — except
          // the split-hub refusal, which opens the cascade double-confirm
          // with the server-reported children count.
          this.loadingID = "";
          let msg = err?.message || "";
          let errData: any = null;
          try {
            errData = JSON.parse(msg);
          } catch {}
          if (errData?.code === "SPLIT_PARENT_HAS_CHILDREN") {
            this.confirmCascadeDelete(tournID, errData.count || 0);
            return;
          }
          if (errData?.code) msg = this.translate.instant(errData.code);
          this.snackBar.open(msg, this.translate.instant("OK"), {
            duration: 7000,
          });
        });
    });
  }
  // Second confirmation for deleting a split hub: the first attempt was
  // refused with the children count — deleting the parent takes the whole
  // night (children and all their results) with it.
  private confirmCascadeDelete(tournID: string, count: number) {
    const total = count + 1;
    this.translate
      .get(
        [
          "CONFIRM_DELETE",
          "SPLIT_DELETE_CASCADE_WARNING",
          "SPLIT_DELETE_CASCADE_BUTTON",
          "CANCEL",
        ],
        { count, total },
      )
      .subscribe((res: any) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              title: res["CONFIRM_DELETE"],
              message: res["SPLIT_DELETE_CASCADE_WARNING"],
              acceptButton: res["SPLIT_DELETE_CASCADE_BUTTON"],
              cancelButton: res["CANCEL"],
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (!accept) return;
            this.loadingID = tournID;
            this.api
              .deleteTournament(tournID, true)
              .then(() => {
                this.loadingID = "";
              })
              .catch((err: any) => {
                this.loadingID = "";
                this.snackBar.open(
                  err?.message || "",
                  this.translate.instant("OK"),
                  { duration: 7000 },
                );
              });
          });
      });
  }
  markIncomplete(tournID: string) {
    // Same spinner wiring as deleteTournament: the callable now writes the club
    // mirror status synchronously, so the promise resolves once the lobby card
    // is ready to reflect "Incomplete".
    this.loadingID = tournID;
    setTimeout(() => {
      this.api.markIncomplete(tournID).then((res) => {
        this.loadingID = "";
      });
    });
  }
  getManualID(playerName: string) {
    if (!playerName || this.latestClub.members[this.clubID + "-" + playerName])
      return null;
    else {
      let newUID = this.clubID + "-" + playerName;
      newUID = newUID.replace(/[.$\[\]#\/]/g, "-");
      return newUID;
    }
  }
  joinNew(playerName: string, extra?: any) {
    let trimmedPlayerName = playerName.trim();
    let newUID: string | null = this.getManualID(trimmedPlayerName);
    if (!newUID) return false;
    else {
      let member: any = {
        memberName: trimmedPlayerName,
        sortName: trimmedPlayerName.toLowerCase(),
        imgURL: defaults.profileImage,
        role: "Member",
        manual: true,
        uid: newUID,
      };
      if (extra) {
        if (extra.firstName) member.firstName = extra.firstName.trim();
        if (extra.lastName) member.lastName = extra.lastName.trim();
      }
      let update: any = {};
      update["members/" + newUID] = member;
      this.clubAF.update(update);
      // Email is staff-only → separate top-level path (see saveMember).
      if (extra && extra.email && extra.email.trim())
        this.nodeSet(
          `staffFields/${this.clubID}/${newUID}/email`,
          extra.email.trim()
        );
      return newUID;
    }
  }
  quickClub(
    dispName: string,
    subLevel: number,
    player_limit: number,
    imgURL?: string
  ) {
    let clubName = "Club " + dispName;
    this.newClub(
      clubName,
      dispName,
      subLevel,
      player_limit,
      imgURL || "/assets/img/anonymous.png"
    );
  }
  newClub(
    clubName: string,
    dispName: string,
    subLevel: number,
    player_limit: number,
    imgURL?: string
  ) {
    let clubID: string = this.util.randomCode(4);
    this.nodeGet(`clubs/${clubID}`).then((snapshot) => {
      if (snapshot.val())
        this.newClub(
          clubName,
          dispName,
          subLevel,
          player_limit,
          imgURL || "/assets/img/anonymous.png"
        );
      else {
        let membersObj: any = {};
        membersObj[this.auth.uid!] = {
          uid: this.auth.uid,
          memberName: dispName,
          sortName: dispName.toLowerCase(),
          role: "Owner",
          imgURL: imgURL || "/assets/img/anonymous.png",
        };
        let clubObj: any = {
          clubID: clubID,
          owner: this.auth.uid,
          clubName: clubName,
          members: membersObj,
          subLevel: subLevel,
          player_limit: player_limit,
        };
        let profClubObj: any = {
          clubID: clubID,
          clubName: clubName,
          role: "Owner",
        };
        let pubUpdate: any = {
          clubID: clubID,
          owner: this.auth.uid,
          clubName: clubName,
        };
        let update: any = {};
        update["clubs/" + clubID] = clubObj;
        update["profiles/" + this.auth.uid + "/clubs/" + clubID] = profClubObj;
        this.rootUpdate(update).then((data) => {
          this.init(clubID);
          this.nodeSet(`public/${clubID}`, pubUpdate);
        });
        this.tracking.track("New Club", { $name: clubName, clubID: clubID });
      }
    });
  }
  // --- Club locations (venues) --------------------------------------------
  // Stored only under clubs/{id}/locations; tournaments carry a
  // {locationID, name, city} snapshot so history keeps its venue even after
  // the location is edited or deleted. Writes stay separate from other
  // club-root writes: the rules grant Directors clubs/$id/locations/* and
  // clubs/$id/homeLocationID only, and multi-path updates authorize per leaf.
  static readonly LOCATION_FIELDS = [
    "name",
    "street",
    "postalCode",
    "city",
    "state",
    "country",
    "telephone",
    "mapURL",
  ] as const;

  private cleanLocationFields(fields: any): any {
    const out: any = {};
    for (const k of ClubService.LOCATION_FIELDS) {
      const v = typeof fields?.[k] === "string" ? fields[k].trim() : "";
      out[k] = v || null;
    }
    // The organizer-pasted map link is rendered as an href for every member:
    // only http(s) URLs are kept (drops javascript:, bare text, etc.).
    if (out.mapURL && !/^https?:\/\//i.test(out.mapURL)) out.mapURL = null;
    return out;
  }

  /**
   * Google Maps search of the typed address, for the "find this address"
   * helper in the location dialog only (members get the pasted mapURL, never
   * a derived search). Null until there is a street or city to search for.
   * With a street address the venue name is left out: a name like "Chez Jeff"
   * makes Maps search for a business by that name instead of the address.
   * Without one, the name is the best lead ("Casino Partouche, Pornic, France").
   */
  locationMapsSearchURL(loc: any): string | null {
    if (!loc) return null;
    const street = (loc.street || "").trim();
    const city = (loc.city || "").trim();
    if (!street && !city) return null;
    const q = [street || loc.name, loc.postalCode, city, loc.state, loc.country]
      .map((p: any) => (p || "").trim())
      .filter(Boolean)
      .join(", ");
    return (
      "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(q)
    );
  }

  /** Human label: name, else city, else country. `full` appends city/state/country. */
  locationLabel(loc: any, full = false): string {
    if (!loc) return "";
    if (!full) return loc.name || loc.city || loc.country || "";
    return [loc.name, loc.city, loc.state, loc.country].filter(Boolean).join(", ");
  }

  /** True when a free-text country reads as the United States (any common alias). */
  isUnitedStates(country: string | null | undefined): boolean {
    const c = (country || "").trim().toLowerCase().replace(/\./g, "");
    return (
      c === "us" ||
      c === "usa" ||
      c === "united states" ||
      c === "united states of america" ||
      c === "états-unis" ||
      c === "etats-unis" ||
      c === "estados unidos" ||
      c === "vereinigte staaten" ||
      c === "stati uniti"
    );
  }

  /**
   * Display record for a tournament's venue snapshot: the live club record
   * when the location still exists (so renames show everywhere at once), the
   * frozen snapshot when it has since been deleted (history keeps its venue).
   */
  resolveLocation(snapshot: any): any {
    if (!snapshot) return null;
    const live = this.locations().find(
      (l: any) => l.locationID === snapshot.locationID
    );
    return live
      ? { ...this.locationSnapshot(live.locationID), mapURL: live.mapURL || null }
      : snapshot;
  }

  /** Snapshot written onto tournaments; null when the id is unknown. */
  locationSnapshot(locationID: string | null | undefined): any {
    if (!locationID) return null;
    const loc = this.locations().find((l: any) => l.locationID === locationID);
    if (!loc) return null;
    return {
      locationID: loc.locationID,
      name: loc.name || null,
      city: loc.city || null,
    };
  }

  createLocation(fields: any, setHome = false): Promise<string> {
    const key: string = this.nodePush(`clubs/${this.clubID}/locations`, undefined).key;
    const createDate = Date.now();
    const update: any = {
      [`locations/${key}`]: {
        locationID: key,
        ...this.cleanLocationFields(fields),
        createDate,
        sort: -createDate,
      },
    };
    if (setHome) update.homeLocationID = key;
    return this.clubAF.update(update).then(() => key);
  }

  updateLocation(locationID: string, fields: any, setHome?: boolean): Promise<void> {
    const clean = this.cleanLocationFields(fields);
    const update: any = {};
    for (const k of ClubService.LOCATION_FIELDS) {
      update[`locations/${locationID}/${k}`] = clean[k];
    }
    if (setHome === true) update.homeLocationID = locationID;
    else if (setHome === false && this.latestClub?.homeLocationID === locationID)
      update.homeLocationID = null;
    return this.clubAF.update(update);
  }

  deleteLocation(locationID: string): Promise<void> {
    const update: any = { [`locations/${locationID}`]: null };
    if (this.latestClub?.homeLocationID === locationID) update.homeLocationID = null;
    return this.clubAF.update(update);
  }

  setHomeLocation(locationID: string | null): Promise<void> {
    return this.clubAF.update({ homeLocationID: locationID || null });
  }

  updateClub(
    newName: string,
    logoURL?: string,
    backURL?: string,
    joinPolicy?: "approval" | "open"
  ) {
    // No optimistic mutation of latestClub (immutable signal); the club signal
    // reflects the change from the RTDB round-trip via api.updateClub.
    let params: any = {
      clubID: this.clubID,
      clubName: newName,
    };
    if (logoURL) params.logoURL = logoURL;
    if (backURL) params.backgroundURL = backURL;
    if (joinPolicy) params.joinPolicy = joinPolicy;
    this.api.updateClub(params);
  }
  async uploadClubLogo(file: File) {
    return this.storageServ
      .uploadBrandImage(`${this.clubID}/clubLogo`, file, CLUB_LOGO_MAX_EDGE)
      .catch((error) => error);
  }
  async uploadBackImg(file: File) {
    return this.storageServ
      .uploadBrandImage(`${this.clubID}/clubBackground`, file, CLUB_BACKGROUND_MAX_EDGE)
      .catch((error) => error);
  }
  removeMemberClubImages(imageType: string) {
    this.api.clubRemoveMemberImages(this.clubID, imageType);
  }
  removeClubImage(clubImgType: string) {
    let path: string;
    if (clubImgType == "CLOCK_BACKGROUND_IMAGE") {
      path = `${this.clubID}/clubBackground`;
      this.clubAF.update({
        backgroundURL: null,
      });
      this.removeMemberClubImages("backgroundURL");
    } else {
      path = `${this.clubID}/clubLogo`;
      this.clubAF.update({
        logoURL: null,
      });
      this.removeMemberClubImages("logoURL");
    }
    this.storageServ.delete(path);
  }

  removeMember(member: any) {
    let clubUpdates: any = {};
    clubUpdates[`clubs/${this.clubID}/members/${member.uid}`] = null;
    clubUpdates[`staffFields/${this.clubID}/${member.uid}`] = null;
    if (!member.manual) this.api.profRemoveClub(this.clubID, member.uid);

    this.rootUpdate(clubUpdates);
  }
  createInvite(email: string): string {
    let inviteRef = this.nodePush(`public/${this.clubID}/invites`, {
      email,
      time: Date.now(),
    });
    let newInvite = inviteRef.key;
    return newInvite;
  }
  getMemberImage(uid: string): string {
    if (
      uid &&
      this.latestClub &&
      this.latestClub.members &&
      this.latestClub.members[uid] &&
      this.latestClub.members[uid].imgURL
    )
      return this.latestClub.members[uid].imgURL;
    else return "/assets/img/badge-players.png";
  }
  deleteClub() {
    let clubID = this.clubID;
    this.deletedClub = clubID;
    let clubName = this.latestClub.clubName;
    this.api.deleteClub(clubID);
    this.translate.get(["CLUB_DELETED"]).subscribe((res: any) => {
      this.snackBar.open(res["CLUB_DELETED"] + ": " + clubName, undefined, {
        duration: 4000,
      });
    });
    this.tracking.track("Delete Club", { clubID: clubID });
  }
  denyRequest(request: any) {
    this.loadingDenyRequest = true;
    this.api.deleteRequest(this.clubID, request.uid, true).then(() => {
      this.loadingDenyRequest = false;
    });
    this.translate.get(["REQUEST_DENIED"]).subscribe((res: any) => {
      this.snackBar.open(
        res["REQUEST_DENIED"] +
          ": " +
          request.dispName +
          "(" +
          request.email +
          ")",
        undefined,
        {
          duration: 4000,
        }
      );
    });
  }

  minClubLevel(level: number) {
    if (this.latestClub && this.latestClub.subLevel >= level) return true;
    else return false;
  }
  getPlayerLimit() {
    if (this.latestClub && this.latestClub.player_limit > 0)
      return Number(this.latestClub.player_limit);
    else return null;
  }
  // clubID is the tournament's OWN club — the selected club can differ from
  // the tournament being edited (multi-club owners), and a mirror write keyed
  // to the wrong/no selection silently strands the lobby copy of params.
  updateTournParams(tournID: string, paramsUpdateValues: any, clubID?: string) {
    const cid = clubID || this.selectedClubId();
    if (cid && tournID)
      this.nodeUpdate(
        `clubs/${cid}/tournaments/${tournID}/params`,
        paramsUpdateValues
      );
  }
  // Writes the member record only. All denormalized copies of the name in the
  // club's tournaments (players incl. re-entry bullets, seats, killer names,
  // winnerName) are repaired server-side by the onMemberNameChange RTDB
  // trigger — which also covers Edit Member dialog renames and profile-rename
  // fan-outs that never went through this method.
  updateMemberName(member: any) {
    var upMemPromise = new Promise((resolve, reject) => {
      let update: any = {};
      let memberPath = "members/" + member.uid + "/";
      update[memberPath + "memberName"] = member.tempName.trim();
      update[memberPath + "sortName"] = member.tempName.trim().toLowerCase();
      this.clubAF.update(update);
      resolve("done");
    });
    return upMemPromise;
  }
  // Staff Notes + Email live at a separate top-level path (not under the
  // club node) because RTDB read grants cascade and the whole club is
  // readable by every member — only this path is restricted to staff/owner.
  // Awaited by the caller BEFORE the edit dialog opens, so staffNotes/staffEmail
  // are populated before the dialog's first change detection — otherwise the
  // raw .once() callback resolves mid-CD and trips NG0100 on the ngModel two-way
  // bindings under zoneless.
  async loadEditMemberStaffFields() {
    let uid = this.editMember && this.editMember.uid;
    if (!uid) return;
    let target = this.editMember;
    const snap = await this.nodeGet(`staffFields/${this.clubID}/${uid}`);
    let data = snap.val() || {};
    target.staffNotes = data.notes || "";
    if (target.manual) target.staffEmail = data.email || "";
  }
  // Full staff-only fields map for the club ({uid: {email, notes}}). Callers
  // must be staff — RTDB rules reject the read otherwise.
  getStaffFields(): Promise<any> {
    return this.nodeGet(`staffFields/${this.clubID}`).then(
      (snap) => snap.val() || {}
    );
  }
  // Returns true (and shows an alert) when another member already has the given
  // email on file. Staff-entered emails live under /staffFields/{clubID}/{uid}.
  // Pass the current member's uid as excludeUid so editing isn't blocked by its
  // own email. Linked accounts don't store an email here, so this only guards
  // against duplicate manually-entered emails.
  async checkDuplicateEmail(
    email: string,
    excludeUid?: string
  ): Promise<boolean> {
    let trimmed = (email || "").trim().toLowerCase();
    if (!trimmed) return false;
    let snap = await this.nodeGet(`staffFields/${this.clubID}`);
    let data = snap.val() || {};
    let conflictUid: string | null = null;
    for (let uid in data) {
      if (uid === excludeUid) continue;
      let e = data[uid] && data[uid].email;
      if (e && e.trim().toLowerCase() === trimmed) {
        conflictUid = uid;
        break;
      }
    }
    if (!conflictUid) return false;
    let m =
      this.latestClub &&
      this.latestClub.members &&
      this.latestClub.members[conflictUid];
    let name = (m && m.memberName) || "";
    this.translate
      .get(["DUPLICATE_EMAIL_TITLE", "DUPLICATE_EMAIL_MSG", "CLOSE"], { name })
      .subscribe((res: any) => {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: res["DUPLICATE_EMAIL_TITLE"],
            message: res["DUPLICATE_EMAIL_MSG"],
            closeButton: res["CLOSE"],
          },
          panelClass: "alert-dialog-container",
        });
      });
    return true;
  }
  saveMember() {
    if (
      this.editMember &&
      this.editMember.uid &&
      this.editMember.memberName &&
      this.editMember.memberName.length
    ) {
      let update: any = {};
      let creating: boolean = false;
      if (!this.latestClub.members[this.editMember.uid]) creating = true;
      if (
        this.editMember.memberName &&
        this.editMember.memberName.length &&
        (creating ||
          this.latestClub.members[this.editMember.uid].memberName !=
            this.editMember.memberName)
      ) {
        update["members/" + this.editMember.uid + "/memberName"] =
          this.editMember.memberName.trim();
        update["members/" + this.editMember.uid + "/sortName"] =
          this.editMember.memberName.trim().toLowerCase();
      }
      // Only write a role change the current user is actually allowed to make
      // (never mint an Owner here; that's the admin-only transfer). Guards the
      // escalation gap even if a crafted value reaches the form.
      if (
        (creating ||
          this.latestClub.members[this.editMember.uid].role !=
            this.editMember.role) &&
        this.assignableRoles().includes(this.editMember.role)
      )
        update["members/" + this.editMember.uid + "/role"] =
          this.editMember.role;
      // First/Last name are denormalized, club-readable fields. For linked
      // members they are owned by the user's profile (see updateUser); staff
      // may only edit them on manual members.
      if (this.editMember.manual) {
        let existing: any = creating
          ? {}
          : this.latestClub.members[this.editMember.uid];
        if (creating || existing.firstName != this.editMember.firstName)
          update["members/" + this.editMember.uid + "/firstName"] = this
            .editMember.firstName
            ? this.editMember.firstName.trim()
            : null;
        if (creating || existing.lastName != this.editMember.lastName)
          update["members/" + this.editMember.uid + "/lastName"] = this
            .editMember.lastName
            ? this.editMember.lastName.trim()
            : null;
      }
      // Per-member website opt-out (Blind Valet Sites). Stored true/absent;
      // the onMemberWebsiteHide trigger rebuilds the public projections.
      {
        const wantHide = !!this.editMember.hideFromWebsite;
        const hadHide = creating
          ? false
          : !!this.latestClub.members[this.editMember.uid].hideFromWebsite;
        if (wantHide !== hadHide) {
          update["members/" + this.editMember.uid + "/hideFromWebsite"] =
            wantHide || null;
        }
      }
      if (this.editMember.croppedImage) {
        // Capture the uid NOW — editMember is shared service state, and by
        // the time the upload resolves it may already point at the NEXT
        // member being edited (photo then lost / written to the wrong uid).
        const memberUid = this.editMember.uid;
        const clubAF = this.clubAF;
        this.storageServ
          .uploadDataUrl(
            `${this.clubID}/members/${memberUid}/memberImg`,
            this.editMember.croppedImage
          )
          .then((url) => {
            update["members/" + memberUid + "/imgURL"] = url;
            clubAF.update(update);
          });
      } else if (creating) {
        update["members/" + this.editMember.uid + "/imgURL"] =
          this.editMember.imgURL;
      }
      if (creating) {
        update["members/" + this.editMember.uid + "/manual"] =
          this.editMember.manual;
        update["members/" + this.editMember.uid + "/uid"] = this.editMember.uid;
      }
      if (Object.keys(update).length) {
        this.clubAF.update(update);
      }
      // Staff Notes (all members) + Email (manual only) → staff-only path.
      // Only write keys that have a defined value so an unloaded field never
      // clobbers existing data; empty string clears the node.
      let staffUpdate: any = {};
      let staffPath = "staffFields/" + this.clubID + "/" + this.editMember.uid;
      if (this.editMember.staffNotes !== undefined)
        staffUpdate[staffPath + "/notes"] = this.editMember.staffNotes || null;
      if (this.editMember.manual && this.editMember.staffEmail !== undefined)
        staffUpdate[staffPath + "/email"] = this.editMember.staffEmail
          ? this.editMember.staffEmail.trim()
          : null;
      if (Object.keys(staffUpdate).length) this.rootUpdate(staffUpdate);
    } else alert("Error saving member");
  }
  addMember() {
    let newUID: string | null = this.getManualID(
      this.editMember.memberName.trim()
    );
    if (
      newUID &&
      this.editMember.memberName &&
      this.editMember.memberName.trim().length
    ) {
      this.editMember.uid = newUID;
      this.saveMember();
    } else {
      alert("Error adding member");
    }
    this.getFirstItemsPage("members", 14);
  }

  getFirstItemsPage(type: string, itemsPerPage: number) {
    if (this.clubID) {
      this.paginationSub?.unsubscribe();
      this.loadingItemsOrderPerPage = true;
      if (type == "members") {
        this.paginationSub = runInInjectionContext(this.injector, () =>
          listVal(
            query(
              ref(this.mdb, "clubs/" + this.clubID + "/" + type),
              orderByKey(),
              startAt("0"),
              limitToFirst(Number(itemsPerPage))
            )
          )
        ).subscribe((res) => {
          if (res) {
            this.membersOrderPerPage = res;
            this.loadingItemsOrderPerPage = false;
          }
        });
      } else if (type == "tournaments") {
        const now = Date.now();
        const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;
        this.paginationSub = runInInjectionContext(this.injector, () =>
          listVal(
            query(
              ref(this.mdb, "clubs/" + this.clubID + "/" + type),
              orderByChild("tournTimestamp"),
              endAt(oneWeekAgo),
              limitToLast(Number(itemsPerPage))
            )
          )
        ).subscribe((res) => {
          if (res) {
            this.tournamentsOrderPerPage = res;
            this.loadingItemsOrderPerPage = false;
          }
        });
      }
      if (this.paginationSub) this.auth.addSub(this.paginationSub);
    }
  }

  getLastItemsPage(type: string, num: any) {
    this.paginationSub?.unsubscribe();
    this.loadingItemsOrderPerPage = true;
    if (type == "members") {
      this.paginationSub = runInInjectionContext(this.injector, () =>
        listVal(
          query(
            ref(this.mdb, "clubs/" + this.clubID + "/" + type),
            orderByKey(),
            limitToLast(Number(num))
          )
        )
      ).subscribe((res) => {
        if (res) {
          this.membersOrderPerPage = res;
          this.loadingItemsOrderPerPage = false;
        }
      });
    } else if (type == "tournaments") {
      let now = Date.now();
      now -= 7 * 24 * 60 * 60 * 1000;
      this.paginationSub = runInInjectionContext(this.injector, () =>
        listVal(
          query(
            ref(this.mdb, "clubs/" + this.clubID + "/" + type),
            orderByChild("tournTimestamp"),
            endAt(now),
            limitToFirst(Number(num))
          )
        )
      ).subscribe((res) => {
        if (res) {
          this.tournamentsOrderPerPage = res;
          this.loadingItemsOrderPerPage = false;
        }
      });
    }
    if (this.paginationSub) this.auth.addSub(this.paginationSub);
  }

  getItemsPerPage(itemId: any, event: string, type: string, itemsPerPage: any) {
    this.loadingItemsOrderPerPage = true;
    if (
      type == "members" &&
      this.membersOrderPerPage &&
      this.membersOrderPerPage.length
    ) {
      this.paginationSub?.unsubscribe();
      if (event == "nextPage") {
        this.paginationSub = runInInjectionContext(this.injector, () =>
          listVal(
            query(
              ref(this.mdb, "clubs/" + this.clubID + "/" + type),
              orderByKey(),
              startAfter(`${itemId}`),
              limitToFirst(Number(itemsPerPage))
            )
          )
        ).subscribe((res) => {
          if (res) {
            this.membersOrderPerPage = res;
            this.loadingItemsOrderPerPage = false;
          }
        });
      } else if (event == "previousPage") {
        this.paginationSub = runInInjectionContext(this.injector, () =>
          listVal(
            query(
              ref(this.mdb, "clubs/" + this.clubID + "/" + type),
              orderByKey(),
              endBefore(`${itemId}`),
              limitToLast(Number(itemsPerPage))
            )
          )
        ).subscribe((res) => {
          if (res) {
            this.membersOrderPerPage = res;
            this.loadingItemsOrderPerPage = false;
          }
        });
      }
    } else if (
      type == "tournaments" &&
      this.tournamentsOrderPerPage &&
      this.tournamentsOrderPerPage.length
    ) {
      this.paginationSub?.unsubscribe();
      let now = Date.now();
      now -= 7 * 24 * 60 * 60 * 1000;
      if (event == "nextPage") {
        this.paginationSub = runInInjectionContext(this.injector, () =>
          listVal(
            query(
              ref(this.mdb, "clubs/" + this.clubID + "/tournaments"),
              orderByChild("tournTimestamp"),
              endBefore(itemId),
              limitToLast(Number(itemsPerPage))
            )
          )
        ).subscribe((res) => {
          if (res) {
            this.tournamentsOrderPerPage = res;
            this.loadingItemsOrderPerPage = false;
          }
        });
      } else if (event == "previousPage") {
        this.paginationSub = runInInjectionContext(this.injector, () =>
          listVal(
            query(
              ref(this.mdb, "clubs/" + this.clubID + "/tournaments"),
              orderByChild("tournTimestamp"),
              startAfter(itemId),
              endAt(now),
              limitToFirst(Number(itemsPerPage))
            )
          )
        ).subscribe((res) => {
          if (res) {
            this.tournamentsOrderPerPage = res;
            this.loadingItemsOrderPerPage = false;
          }
        });
      }
    }
    if (this.paginationSub) this.auth.addSub(this.paginationSub);
  }
}
